import { useMemo } from 'react';
import useQuery from '@/Hooks/UseQuery';
import { CandidateTabs } from '@/Models/CandidateOverview';
import ApplicantsOverviewApplicantStatusFilterEnum from '../../../Models/ApplicantsOverviewApplicantStatusFilterEnum';
import AdvancedFilterTypes from '@/Models/AdvancedFilterTypes';
import ApplicantsOverviewHiringStatusFilterEnum from '../../../Models/ApplicantsOverviewHiringStatusFilterEnum';
import ApplicantsOverviewSortByEnum from '../../../Models/ApplicantsOverviewSortByEnum';

export type CandidatesOverviewQueryParams =
    | 'page'
    | 'tab'
    | 'filterBy'
    | keyof typeof AdvancedFilterTypes
    | 'search'
    | 'hiringStatusFilter'
    | 'sortBy';
export const CandidatesOverviewQueryParamsSeparator = ',';
export const useCandidatesOverviewQuery = useQuery<CandidatesOverviewQueryParams>;

// parse query to applicant's overview filter state, page position, etc
const useCandidatesOverviewQueryParams = () => {
    const pageNumberQuery = useCandidatesOverviewQuery('page');
    const candidateTabQuery = useCandidatesOverviewQuery('tab');
    const filterByQuery = useCandidatesOverviewQuery('filterBy');
    const hiringStatusFilterQuery = useCandidatesOverviewQuery('hiringStatusFilter');
    const search = useCandidatesOverviewQuery('search') ?? '';
    const sortByQuery = useCandidatesOverviewQuery('sortBy');

    const countryQuery = useCandidatesOverviewQuery('Country');
    const fieldOfEducationQuery = useCandidatesOverviewQuery('FieldOfEducation');
    const highestLevelOfEducationQuery = useCandidatesOverviewQuery('HighestLevelOfEducation');
    const jobTypeQuery = useCandidatesOverviewQuery('JobType');

    return useMemo(() => {
        let candidateTab: CandidateTabs;

        const pageNumber = +pageNumberQuery! || 0;
        const sortBy: ApplicantsOverviewSortByEnum | undefined = +sortByQuery! || undefined;
        const splitQuery = (query: string | null) => query?.split(CandidatesOverviewQueryParamsSeparator) || [];
        const countries = splitQuery(countryQuery);
        const fieldOfEducations = splitQuery(fieldOfEducationQuery);
        const highestLevelOfEducations = splitQuery(highestLevelOfEducationQuery);
        const jobTypes = splitQuery(jobTypeQuery);
        const filterBy = splitQuery(filterByQuery) as ApplicantsOverviewApplicantStatusFilterEnum[];
        const hiringStatusFilter = splitQuery(hiringStatusFilterQuery) as ApplicantsOverviewHiringStatusFilterEnum[];

        switch (candidateTabQuery) {
            case CandidateTabs.Hired:
                candidateTab = CandidateTabs.Hired;
                break;
            case CandidateTabs.Rejected:
                candidateTab = CandidateTabs.Rejected;
                break;
            case CandidateTabs.Open:
            default:
                candidateTab = CandidateTabs.Open;
                break;
        }

        return {
            pageNumber,
            candidateTab,
            filterBy,
            hiringStatusFilter,
            countries,
            fieldOfEducations,
            highestLevelOfEducations,
            jobTypes,
            search,
            sortBy,
        };
    }, []);
};

export default useCandidatesOverviewQueryParams;
